import React from "react";
import { motion } from "framer-motion";
import { useMicrospecThemeLanguageContext } from "../../../MicrospecDynamicUI/Themes";
import { Box, Typography, Container, Rating } from "@mui/material";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";

const avatarGradients = [
  "from-[#7393B3] to-[#8B5CF6]",
  "from-[#8B5CF6] to-[#C084FC]",
  "from-[#C084FC] to-[#EC4899]",
];

const TestimonialsSection = () => {
  const { localizationProvider, dir } = useMicrospecThemeLanguageContext();

  const title = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.testimonials.title",
  });
  const subtitle = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.testimonials.subtitle",
  });

  const testimonials = [0, 1, 2].map((i) => ({
    quote: localizationProvider.translateByType({
      type: "Localized",
      text: `landing.testimonials.items.${i}.quote`,
    }),
    name: localizationProvider.translateByType({
      type: "Localized",
      text: `landing.testimonials.items.${i}.name`,
    }),
    role: localizationProvider.translateByType({
      type: "Localized",
      text: `landing.testimonials.items.${i}.role`,
    }),
  }));

  return (
    <Box className="py-28 relative overflow-hidden" sx={{ background: "transparent" }}>
      {/* Atmospheric blobs */}
      <motion.div
        className="absolute top-[20%] left-[-5%] w-[30%] h-[40%] rounded-full bg-[#D4C5F9]/25 blur-[110px] pointer-events-none"
        animate={{ scale: [1, 1.06, 1] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-[10%] right-[-5%] w-[25%] h-[35%] rounded-full bg-[#F5E1EC]/20 blur-[90px] pointer-events-none"
        animate={{ scale: [1, 1.08, 1] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut", delay: 3 }}
      />

      <Container maxWidth="xl" dir={dir}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 px-4"
        >
          <Typography
            variant="overline"
            className="font-bold tracking-[0.2em] mb-3 block"
            sx={{
              background: "linear-gradient(135deg, #7393B3, #8B5CF6)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            TESTIMONIALS
          </Typography>
          <Typography
            variant="h2"
            className="text-4xl md:text-5xl font-bold tracking-tight text-[#2D1B4E] mb-4"
          >
            {title}
          </Typography>
          <Typography className="text-lg text-[#6B5B7B] max-w-2xl mx-auto">
            {subtitle}
          </Typography>
        </motion.div>

        <div className="px-4 md:px-8">
          {/* Featured first testimonial — enhanced dark card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
            className="mb-6"
          >
            <div className="relative p-10 md:p-14 rounded-[2rem] bg-gradient-to-br from-[#0A0F2E] to-[#131836] border border-white/5 shadow-2xl shadow-[#8B5CF6]/10 overflow-hidden">
              {/* Vivid ambient glows */}
              <motion.div
                className="absolute top-0 right-0 w-[50%] h-[50%] rounded-full bg-[#8B5CF6]/12 blur-[80px] pointer-events-none"
                animate={{ opacity: [0.08, 0.16, 0.08] }}
                transition={{ duration: 6, repeat: Infinity }}
              />
              <motion.div
                className="absolute bottom-0 left-0 w-[40%] h-[40%] rounded-full bg-[#7393B3]/10 blur-[60px] pointer-events-none"
                animate={{ opacity: [0.06, 0.12, 0.06] }}
                transition={{ duration: 8, repeat: Infinity, delay: 2 }}
              />

              <div className="relative z-10 flex flex-col md:flex-row items-start gap-8">
                <FormatQuoteIcon
                  sx={{
                    fontSize: 56,
                    color: "#8B5CF6",
                    opacity: 0.4,
                    transform: "scaleX(-1)",
                    flexShrink: 0,
                    filter: "drop-shadow(0 0 8px rgba(139,92,246,0.3))",
                  }}
                />
                <div className="flex-1">
                  <Typography className="text-white/90 leading-relaxed text-lg md:text-xl mb-8 italic font-light">
                    &ldquo;{testimonials[0].quote}&rdquo;
                  </Typography>
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-12 h-12 rounded-full bg-gradient-to-br ${avatarGradients[0]} flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#8B5CF6]/25`}
                    >
                      <Typography className="text-white font-bold text-base">
                        {testimonials[0].name.charAt(0)}
                      </Typography>
                    </div>
                    <div>
                      <Typography className="font-semibold text-white text-sm">
                        {testimonials[0].name}
                      </Typography>
                      <Typography className="text-white/40 text-xs">
                        {testimonials[0].role}
                      </Typography>
                    </div>
                    <div className="ml-auto">
                      <Rating
                        value={5}
                        readOnly
                        size="small"
                        sx={{ "& .MuiRating-iconFilled": { color: "#FBBF24" } }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Two smaller frosted glass testimonial cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonials.slice(1).map((testimonial, index) => (
              <motion.div
                key={index + 1}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: (index + 1) * 0.15 }}
              >
                <div
                  className="h-full p-8 rounded-[2rem] backdrop-blur-xl transition-all duration-300 flex flex-col justify-between group hover:-translate-y-1"
                  style={{
                    background: "rgba(255,255,255,0.45)",
                    border: "1px solid rgba(139,92,246,0.08)",
                    boxShadow: "0 4px 20px rgba(139,92,246,0.04), 0 0 0 1px rgba(255,255,255,0.5) inset",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = "0 16px 50px rgba(139,92,246,0.1), 0 0 0 1px rgba(255,255,255,0.6) inset";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = "0 4px 20px rgba(139,92,246,0.04), 0 0 0 1px rgba(255,255,255,0.5) inset";
                  }}
                >
                  <div>
                    <div className="flex items-center justify-between mb-5">
                      <FormatQuoteIcon
                        sx={{
                          fontSize: 32,
                          color: "#8B5CF6",
                          opacity: 0.3,
                          transform: "scaleX(-1)",
                          filter: "drop-shadow(0 0 4px rgba(139,92,246,0.2))",
                        }}
                      />
                      <Rating
                        value={5}
                        readOnly
                        size="small"
                        sx={{ "& .MuiRating-iconFilled": { color: "#FBBF24" } }}
                      />
                    </div>
                    <Typography className="text-[#4A3B6B] leading-relaxed mb-6 text-[15px] italic">
                      &ldquo;{testimonial.quote}&rdquo;
                    </Typography>
                  </div>
                  <div className="flex items-center gap-3 pt-5 border-t border-[#8B5CF6]/8">
                    <div
                      className={`w-10 h-10 rounded-full bg-gradient-to-br ${avatarGradients[index + 1]} flex items-center justify-center flex-shrink-0 shadow-md`}
                    >
                      <Typography className="text-white font-bold text-sm">
                        {testimonial.name.charAt(0)}
                      </Typography>
                    </div>
                    <div>
                      <Typography className="font-semibold text-[#2D1B4E] text-sm">
                        {testimonial.name}
                      </Typography>
                      <Typography className="text-[#8B7BA0] text-xs">
                        {testimonial.role}
                      </Typography>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Container>
    </Box>
  );
};

export default TestimonialsSection;
